import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  ArrowDownToLine, ArrowUpFromLine, Activity, Users, Server,
  TrendingUp, RefreshCw, ChevronRight, Zap, ArrowUpRight,
  ArrowDownRight, Minus, Wallet,
} from "lucide-react";
import TransactionChart from "@/shared/components/dashboard/TransactionChart";
import RecentTransactions from "@/shared/components/dashboard/RecentTransactions";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";
import * as adminService from "@/shared/api/services/adminService";
import * as providerService from "@/shared/api/services/providerService";
import ProviderStatus from "@/shared/components/dashboard/ProviderStatus";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import ChartLoader from "@/shared/components/dashboard/ChartLoader";
import { getDateRange } from "@/shared/utils/dateFilters";
import utilities from "../utils/utilities";
import gsap from "gsap";

/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS  — single source of truth
═══════════════════════════════════════════════════════════════════════════ */
const C = {
  /* backgrounds */
  bg: "#060C18",
  surface: "#0A1628",
  card: "#0E1C35",
  cardHigh: "#122040",
  hover: "#162847",

  /* borders */
  border: "rgba(255,255,255,0.055)",
  borderMid: "rgba(255,255,255,0.1)",
  borderCyan: "rgba(34,211,238,0.35)",

  /* accent palette */
  cyan: "#22D3EE",
  cyanBright: "#67E8F9",
  cyanGlow: "rgba(34,211,238,0.18)",
  cyanGlowSm: "rgba(34,211,238,0.09)",
  cyanShadow: "0 0 30px rgba(34,211,238,0.22), 0 0 60px rgba(34,211,238,0.08)",

  blue: "#3B82F6",
  blueGlow: "rgba(59,130,246,0.15)",

  emerald: "#10B981",
  emeraldGlow: "rgba(16,185,129,0.15)",
  emeraldSoft: "rgba(16,185,129,0.08)",

  purple: "#A78BFA",
  purpleGlow: "rgba(167,139,250,0.15)",
  purpleSoft: "rgba(167,139,250,0.08)",

  amber: "#FBBF24",
  amberGlow: "rgba(251,191,36,0.15)",
  amberSoft: "rgba(251,191,36,0.08)",

  rose: "#FB7185",
  roseGlow: "rgba(251,113,133,0.15)",
  roseSoft: "rgba(251,113,133,0.08)",

  /* text */
  t1: "#EFF6FF",
  t2: "#93B4D4",
  t3: "#4D6E90",
  t4: "#2A4060",
};

/* ═══════════════════════════════════════════════════════════════════════════
   GLOBAL CSS INJECTED ONCE
   Overrides DateFilter / any white UI component to match dark theme
═══════════════════════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

  /* ── DateFilter dark override ── */
  .dash-date-filter,
  .dash-date-filter * {
    font-family: 'Outfit', system-ui, sans-serif !important;
  }
  .dash-date-filter button,
  .dash-date-filter [role="button"],
  .dash-date-filter select,
  .dash-date-filter [class*="trigger"],
  .dash-date-filter [class*="button"],
  .dash-date-filter > div > button {
    background: #0E1C35 !important;
    background-color: #0E1C35 !important;
    border: 1px solid rgba(255,255,255,0.08) !important;
    color: #93B4D4 !important;
    border-radius: 12px !important;
  }
  .dash-date-filter button:hover,
  .dash-date-filter [role="button"]:hover {
    background: #162847 !important;
    border-color: rgba(34,211,238,0.3) !important;
    color: #EFF6FF !important;
  }
  /* Dropdown popup */
  .dash-date-filter [role="listbox"],
  .dash-date-filter [data-radix-popper-content-wrapper],
  .dash-date-filter [class*="content"],
  .dash-date-filter [class*="popover"],
  .dash-date-filter [class*="dropdown"] {
    background: #0E1C35 !important;
    border: 1px solid rgba(255,255,255,0.1) !important;
    border-radius: 14px !important;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6) !important;
  }
  .dash-date-filter [role="option"],
  .dash-date-filter [class*="item"] {
    color: #93B4D4 !important;
    background: transparent !important;
  }
  .dash-date-filter [role="option"]:hover,
  .dash-date-filter [class*="item"]:hover,
  .dash-date-filter [role="option"][data-highlighted],
  .dash-date-filter [class*="item"][data-highlighted] {
    background: rgba(34,211,238,0.1) !important;
    color: #22D3EE !important;
  }
  .dash-date-filter [class*="separator"] {
    background: rgba(255,255,255,0.06) !important;
  }
  /* Calendar dark */
  .dash-date-filter [class*="calendar"],
  .dash-date-filter [class*="Calendar"] {
    background: #0A1628 !important;
    color: #EFF6FF !important;
  }
  .dash-date-filter [class*="day"] {
    color: #93B4D4 !important;
  }
  .dash-date-filter [class*="day"]:hover {
    background: rgba(34,211,238,0.12) !important;
    color: #22D3EE !important;
  }
  .dash-date-filter [class*="day"][class*="selected"],
  .dash-date-filter [class*="day"][aria-selected="true"] {
    background: #22D3EE !important;
    color: #060C18 !important;
  }
  /* Scrollbar */
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.2); border-radius: 99px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(34,211,238,0.4); }
`;

/* ═══════════════════════════════════════════════════════════════════════════
   ANIMATED COUNTER  (gsap.to on plain obj, no context wrapper)
═══════════════════════════════════════════════════════════════════════════ */
const AnimatedCounter = memo(({ value, prefix = "", suffix = "", decimals = 0 }) => {
  const spanRef = useRef(null);
  const tweenRef = useRef(null);
  const objRef = useRef({ val: 0 });

  useEffect(() => {
    if (objRef.current.val === parseFloat(value)) return;
    const node = spanRef.current;
    if (!node) return;
    if (tweenRef.current) tweenRef.current.kill();

    tweenRef.current = gsap.to(objRef.current, {
      val: parseFloat(value) || 0,
      duration: 1.4,
      ease: "power4.out",
      onUpdate() {
        const v = objRef.current.val;
        node.textContent =
          prefix +
          (decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString()) +
          suffix;
      },
    });
    return () => tweenRef.current?.kill();
  }, [value, prefix, suffix, decimals]);

  const fallback =
    prefix +
    (decimals > 0 ? (parseFloat(value) || 0).toFixed(decimals)
      : (parseFloat(value) || 0).toLocaleString()) +
    suffix;

  return <span ref={spanRef} className="tabular-nums">{fallback}</span>;
});

/* ═══════════════════════════════════════════════════════════════════════════
   TILT CARD WRAPPER  — 3D mouse-tracking tilt effect (lightweight transform only)
═══════════════════════════════════════════════════════════════════════════ */
const TiltCard = memo(({ children, className = "", style = {}, intensity = 4 }) => { // lowered from 8 -> 4
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 250, damping: 30 }); // slightly lighter spring
  const sy = useSpring(y, { stiffness: 250, damping: 30 });
  const rx = useTransform(sy, [-0.5, 0.5], [`${intensity}deg`, `-${intensity}deg`]);
  const ry = useTransform(sx, [-0.5, 0.5], [`-${intensity}deg`, `${intensity}deg`]);

  const handleMove = useCallback((e) => {
    if (window.innerWidth < 1024) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  }, [x, y]);

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", ...style }}
      className={className}
    >
      {children}
    </motion.div>
  );
});


/* ═══════════════════════════════════════════════════════════════════════════
   STAT CARD  — premium with 3D tilt + cyan glow on hover
═══════════════════════════════════════════════════════════════════════════ */
const CARD_CONFIGS = {
  cyan: { accent: C.cyan, glow: C.cyanGlow, soft: C.cyanGlowSm, shadow: "0 0 40px rgba(34,211,238,0.2)" },
  emerald: { accent: C.emerald, glow: C.emeraldGlow, soft: C.emeraldSoft, shadow: "0 0 40px rgba(16,185,129,0.2)" },
  purple: { accent: C.purple, glow: C.purpleGlow, soft: C.purpleSoft, shadow: "0 0 40px rgba(167,139,250,0.2)" },
  amber: { accent: C.amber, glow: C.amberGlow, soft: C.amberSoft, shadow: "0 0 40px rgba(251,191,36,0.2)" },
};

const StatCard = memo(({
  title, rawValue, prefix = "", suffix = "", decimals = 0,
  change, changeType, icon: Icon, colorKey, index,
}) => {
  const [hovered, setHovered] = useState(false);
  const cfg = CARD_CONFIGS[colorKey] || CARD_CONFIGS.cyan;

  const changeColor = changeType === "positive" ? C.emerald
    : changeType === "negative" ? C.rose : C.t3;
  const changeBg = changeType === "positive" ? C.emeraldSoft
    : changeType === "negative" ? C.roseSoft : "rgba(255,255,255,0.04)";
  const ChangeIcon = changeType === "positive" ? ArrowUpRight
    : changeType === "negative" ? ArrowDownRight : Minus;

  return (
    <motion.div
      initial={{ opacity: 0, y: 32, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, delay: 0.1 + index * 0.1, ease: [0.16, 1, 0.3, 1] }}
    >
      <TiltCard intensity={6}>
        <motion.div
          onHoverStart={() => setHovered(true)}
          onHoverEnd={() => setHovered(false)}
          animate={{
            boxShadow: hovered
              ? `${cfg.shadow}, inset 0 1px 0 rgba(255,255,255,0.08)`
              : "inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
          transition={{ duration: 0.3 }}
          className="relative rounded-2xl overflow-hidden cursor-default"
          style={{
            background: `linear-gradient(155deg, ${C.cardHigh} 0%, ${C.card} 60%, ${C.surface} 100%)`,
            border: `1px solid ${hovered ? cfg.accent + "44" : C.border}`,
            transition: "border-color 0.3s ease",
          }}
        >
          {/* ── Animated glow halo behind card ── */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            animate={{ opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.25 }}
            style={{
              background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${cfg.glow} 0%, transparent 70%)`,
            }}
          />

          {/* ── Top edge glow line ── */}
          <motion.div
            className="absolute top-0 inset-x-0 h-px pointer-events-none"
            animate={{ opacity: hovered ? 1 : 0.4 }}
            transition={{ duration: 0.3 }}
            style={{
              background: `linear-gradient(90deg, transparent 5%, ${cfg.accent}88 50%, transparent 95%)`,
            }}
          />

          {/* ── Diagonal geometry decoration ── */}
          <div
            className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${cfg.soft} 0%, transparent 65%)`,
              transform: "translate(0,0)",
            }}
          />

          <div className="relative p-6 pb-5">
            {/* Row 1: icon + badge */}
            <div className="flex items-start justify-between mb-6">
              {/* Icon with animated ring on hover */}
              <motion.div
                animate={{
                  boxShadow: hovered ? `0 0 0 4px ${cfg.soft}, 0 0 20px ${cfg.glow}` : "0 0 0 0px transparent",
                }}
                transition={{ duration: 0.3 }}
                className="w-11 h-11 rounded-2xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${cfg.soft} 0%, transparent 100%)`,
                  border: `1px solid ${cfg.accent}30`,
                }}
              >
                <Icon size={20} style={{ color: cfg.accent }} />
              </motion.div>

              {/* Change badge */}
              <motion.span
                animate={{ scale: hovered ? 1.05 : 1 }}
                transition={{ type: "spring", stiffness: 400 }}
                className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-xl"
                style={{ color: changeColor, background: changeBg }}
              >
                <ChangeIcon size={10} strokeWidth={3} />
                {change}
              </motion.span>
            </div>

            {/* Label */}
            <p
              className="text-[11px] font-semibold tracking-[0.12em] uppercase mb-2"
              style={{ color: C.t3 }}
            >
              {title}
            </p>

            {/* Value */}
            <motion.p
              animate={{ color: hovered ? cfg.accent : C.t1 }}
              transition={{ duration: 0.3 }}
              className="text-[1.75rem] font-bold tracking-tight leading-none"
            >
              <AnimatedCounter
                value={rawValue}
                prefix={prefix}
                suffix={suffix}
                decimals={decimals}
              />
            </motion.p>

            {/* Bottom micro bar */}
            <div
              className="mt-4 h-1 rounded-full overflow-hidden"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: hovered ? "100%" : "65%" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="h-full rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${cfg.accent}99, ${cfg.accent}22)`,
                }}
              />
            </div>
          </div>
        </motion.div>
      </TiltCard>
    </motion.div>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   GLASS PANEL
═══════════════════════════════════════════════════════════════════════════ */
const GlassPanel = memo(({ children, className = "", glowOnHover = false }) => {
  const [h, setH] = useState(false);
  return (
    <motion.div
      onHoverStart={() => glowOnHover && setH(true)}
      onHoverEnd={() => glowOnHover && setH(false)}
      className={`rounded-2xl relative overflow-hidden ${className}`}
      animate={glowOnHover ? {
        borderColor: h ? "rgba(34,211,238,0.18)" : C.border,
        boxShadow: h ? "0 0 40px rgba(34,211,238,0.07)" : "none",
      } : {}}
      transition={{ duration: 0.3 }}
      style={{
        background: `linear-gradient(155deg, ${C.cardHigh} 0%, ${C.card} 100%)`,
        border: `1px solid ${C.border}`,
      }}
    >
      {/* Top edge shimmer */}
      <div
        className="absolute top-0 inset-x-0 h-px pointer-events-none"
        style={{ background: `linear-gradient(90deg, transparent, rgba(34,211,238,0.18), transparent)` }}
      />
      {children}
    </motion.div>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION HEADER
═══════════════════════════════════════════════════════════════════════════ */
const SectionHeader = memo(({ title, action, actionLabel }) => (
  <div className="flex items-center justify-between mb-5">
    <div className="flex items-center gap-3">
      <div
        className="w-[3px] h-5 rounded-full"
        style={{ background: `linear-gradient(180deg, ${C.cyan} 0%, ${C.blue}88 100%)` }}
      />
      <span
        className="text-[11px] font-bold tracking-[0.14em] uppercase"
        style={{ color: C.t3 }}
      >
        {title}
      </span>
    </div>
    {action && (
      <motion.button
        onClick={action}
        whileHover={{ x: 3 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: "spring", stiffness: 600, damping: 25 }}
        className="flex items-center gap-1.5 text-[11px] font-semibold rounded-lg px-2.5 py-1.5"
        style={{
          color: C.cyan,
          background: C.cyanGlowSm,
          border: `1px solid rgba(34,211,238,0.12)`,
          cursor: "pointer",
        }}
      >
        {actionLabel}
        <ChevronRight size={12} strokeWidth={2.5} />
      </motion.button>
    )}
  </div>
));

/* ═══════════════════════════════════════════════════════════════════════════
   QUICK ACTION ROW  — animated arrow + glow on hover
═══════════════════════════════════════════════════════════════════════════ */
const QuickActionRow = memo(({ to, icon: Icon, iconBg, iconColor, label, sublabel }) => {
  const [h, setH] = useState(false);
  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      <motion.div
        onHoverStart={() => setH(true)}
        onHoverEnd={() => setH(false)}
        animate={{
          backgroundColor: h ? C.hover : "transparent",
          x: h ? 4 : 0,
        }}
        transition={{ type: "spring", stiffness: 500, damping: 35 }}
        className="flex items-center gap-3.5 px-3 py-3 rounded-xl cursor-pointer"
        style={{ border: `1px solid ${h ? "rgba(34,211,238,0.1)" : "transparent"}` }}
      >
        <motion.div
          animate={{
            boxShadow: h ? `0 0 14px ${iconColor}55` : "none",
          }}
          transition={{ duration: 0.25 }}
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: iconBg, border: `1px solid ${iconColor}22` }}
        >
          <Icon size={16} style={{ color: iconColor }} />
        </motion.div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight" style={{ color: h ? C.t1 : C.t2 }}>
            {label}
          </p>
          <p className="text-[11px] truncate mt-0.5" style={{ color: C.t3 }}>{sublabel}</p>
        </div>

        <motion.div
          animate={{ opacity: h ? 1 : 0, x: h ? 0 : -4 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronRight size={14} style={{ color: C.cyan }} />
        </motion.div>
      </motion.div>
    </Link>
  );
});

/* REFRESH BUTTON*/
const RefreshButton = memo(({ onClick, spinning }) => {
  const [h, setH] = useState(false);
  return (
    <motion.button
      onClick={onClick}
      onHoverStart={() => setH(true)}
      onHoverEnd={() => setH(false)}
      whileTap={{ scale: 0.92 }}
      animate={{
        boxShadow: h ? `0 0 20px ${C.cyanGlow}` : "none",
        borderColor: h ? "rgba(34,211,238,0.35)" : C.border,
      }}
      transition={{ duration: 0.25 }}
      className="w-9 h-9 rounded-xl flex items-center justify-center"
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        color: h ? C.cyan : C.t3,
        cursor: "pointer",
      }}
    >
      <motion.div
        animate={spinning ? { rotate: 360 } : { rotate: 0 }}
        transition={spinning
          ? { duration: 0.7, ease: "linear", repeat: Infinity }
          : { duration: 0.3 }}
      >
        <RefreshCw size={15} />
      </motion.div>
    </motion.button>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   LIVE PULSE DOT*/
const LiveDot = () => (
  <span className="relative flex items-center justify-center w-3 h-3">
    <motion.span
      animate={{ scale: [1, 1.8, 1], opacity: [0.6, 0, 0.6] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      className="absolute inline-flex w-full h-full rounded-full"
      style={{ background: C.emerald }}
    />
    <span className="relative inline-flex w-1.5 h-1.5 rounded-full" style={{ background: C.emerald }} />
  </span>
);

/*GRID BACKGROUND DECORATION  (pure CSS, zero JS cost)*/
const GridBg = () => (
  <div
    aria-hidden
    className="fixed inset-0 pointer-events-none"
    style={{
      backgroundImage: `
        linear-gradient(rgba(34,211,238,0.025) 1px, transparent 1px),
        linear-gradient(90deg, rgba(34,211,238,0.025) 1px, transparent 1px)
      `,
      backgroundSize: "60px 60px",
      maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)",
    }}
  />
);

/* ADMIN DASHBOARD  — main component*/
export default function AdminDashboard() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [dateFilter, setDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const headerRef = useRef(null);

  /* Inject global CSS once */
  useEffect(() => {
    const id = "dash-global-styles";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  /* GSAP header entrance — scoped correctly to DOM element */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".gsap-h", { opacity: 0, y: -20 }, {
        opacity: 1,
        y: 0,
        duration: 0.5, // reduced from 0.6
        stagger: 0.08,
        ease: "power2.out",
      });
    }, headerRef);
    return () => ctx.revert();
  }, []);

  const dateRange = useMemo(() => {
    return getDateRange(dateFilter, customDateRange);
  }, [dateFilter, customDateRange]);

  /*Queries*/
  const { data: dashboardStats, refetch: refetchDashboardStats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => adminService.getDashboardLiveMetrics(),
  });

  const { data: providers = [], isLoading: isProvidersLoading } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  const { data: transactions = [], refetch: refetchTransactions } = useQuery({
    queryKey: ["transactions", page, pageSize],
    queryFn: () => combinedTransactionService.getCombinedQRCodeAndWitData(
      page + 1,
      pageSize
    ),
  });

  const { data: chartData = [], isLoading: isChartLoading, isFetching: isChartFetching, refetch: refetchChart } = useQuery({
    queryKey: [
      "merchant-chart",
      dateRange?.startDate?.toISOString?.(),
      dateRange?.endDate?.toISOString?.(),
    ],
    queryFn: () =>
      adminService.getMerchantChart({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
        merchantIds: [],
      }),
    enabled: !!dateRange?.startDate && !!dateRange?.endDate,
    placeholderData: (previousData) => previousData ?? [],
  });

  /*Change metas */
  const depositMeta = utilities.getChangeMeta(dashboardStats?.depositChange ?? 0);
  const withdrawalMeta = utilities.getChangeMeta(dashboardStats?.withdrawalChange ?? 0);
  const successMeta = utilities.getChangeMeta(dashboardStats?.successRateChange ?? 0);
  const merchantMeta = utilities.getChangeMeta(dashboardStats?.activeMerchantChange ?? 0);
  const adminMeta = utilities.getChangeMeta(dashboardStats?.adminBalanceChange ?? 0);

  /*Refresh */
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);

    Promise.all([
      refetchDashboardStats(),
      refetchTransactions(),
      refetchChart(),
    ]).finally(() => {
      setTimeout(() => setIsRefreshing(false), 900);
    });
  }, [refetchDashboardStats, refetchTransactions, refetchChart]);


  /* RENDER*/
  return (
    <div
      className="min-h-screen w-full"
      style={{ background: C.bg, fontFamily: "'Outfit', 'Inter', system-ui, sans-serif" }}
    >
      {/* Decorative grid bg */}
      <GridBg />

      {/* Static radial ambient glow — no animation, zero cost */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 1000px 600px at 0% 0%,   rgba(34,211,238,0.04) 0%, transparent 65%),
            radial-gradient(ellipse 800px  500px at 100% 100%, rgba(59,130,246,0.04)  0%, transparent 65%),
            radial-gradient(ellipse 600px  400px at 60% 40%,  rgba(167,139,250,0.025) 0%, transparent 60%)
          `,
        }}
      />

      <div className="relative z-10 max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6">

        {/* ════ HEADER ════════════════════════════════════════════════════ */}
        <div
          ref={headerRef}
          className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
        >
          {/* Left */}
          <div className="gsap-h" style={{ opacity: 0 }}>
            <div className="flex items-center gap-2.5 mb-2">
              <LiveDot />
              <span
                className="text-[10px] font-bold tracking-[0.22em] uppercase"
                style={{ color: C.t3 }}
              >
                Live · Payment Ops
              </span>
            </div>
            <h1
              className="text-4xl font-bold tracking-tight"
              style={{
                color: C.t1,
                textShadow: `0 0 60px rgba(34,211,238,0.15)`,
              }}
            >
              {t("dashboard")}
            </h1>
            <p className="text-sm mt-1 font-medium" style={{ color: C.t3 }}>
              {t("paymentOrchestration")}
            </p>
          </div>

          {/* Right — DateFilter wrapped in dark override class */}
          <div className="gsap-h flex items-center gap-2.5" style={{ opacity: 0 }}>
            <div className="dash-date-filter">
              <DateFilter
                value={dateFilter}
                onChange={setDateFilter}
                customDateRange={customDateRange}
                onCustomDateRangeChange={setCustomDateRange}
              />
            </div>
            <RefreshButton onClick={handleRefresh} spinning={isRefreshing} />
          </div>
        </div>

        {/* ════ STAT CARDS ════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard
            title={"Fee Balance"}
            rawValue={dashboardStats?.adminBalance ?? 0}
            prefix="฿"
            change={adminMeta.changeText}
            changeType={adminMeta.changeType}
            icon={Wallet}
            colorKey="blue"
            index={4}
          />
          <StatCard
            title={t("totalDeposits")}
            rawValue={dashboardStats?.totalDepositsAmount ?? 0}
            prefix="฿"
            change={depositMeta.changeText}
            changeType={depositMeta.changeType}
            icon={ArrowDownToLine}
            colorKey="cyan"
            index={0}
          />
          <StatCard
            title={t("totalWithdrawals")}
            rawValue={dashboardStats?.totalWithdrawalsAmount ?? 0}
            prefix="฿"
            change={withdrawalMeta.changeText}
            changeType={withdrawalMeta.changeType}
            icon={ArrowUpFromLine}
            colorKey="emerald"
            index={1}
          />
          <StatCard
            title={t("successRate")}
            rawValue={dashboardStats?.successRate ?? 0}
            suffix="%"
            decimals={2}
            change={successMeta.changeText}
            changeType={successMeta.changeType}
            icon={Activity}
            colorKey="purple"
            index={2}
          />
          <StatCard
            title={t("activeMerchants")}
            rawValue={dashboardStats?.totalActiveMerchants ?? 0}
            change={merchantMeta.changeText}
            changeType={merchantMeta.changeType}
            icon={Users}
            colorKey="amber"
            index={3}
          />
        </div>

        {/* ════ CHARTS + PROVIDER STATUS ══════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-5"
        >
          <GlassPanel className="lg:col-span-2 p-6 h-full relative" glowOnHover>
            {isChartLoading || isChartFetching ? (
              <ChartLoader />
            ) : (
              <TransactionChart data={chartData} />
            )}
          </GlassPanel>

          <GlassPanel className="p-6 h-full" glowOnHover>
            <ProviderStatus
              providers={providers}
              isLoading={isProvidersLoading}
              onViewAll={() => navigate(createPageUrl("Providers"))}
            />
          </GlassPanel>
        </motion.div>

        {/* ════ RECENT TRANSACTIONS + SIDEBAR ════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-5"
        >
          {/* Table */}
          <GlassPanel className="lg:col-span-2 p-6" glowOnHover>
            <RecentTransactions
              transactions={transactions.data}
              onViewAll={() => navigate(createPageUrl("Transactions"))}
            />
          </GlassPanel>

          {/* Right column */}
          <div className="flex flex-col gap-4">

            {/* Quick Actions */}
            <GlassPanel className="p-5 flex-1">
              <SectionHeader title={t("quickActions") || "Quick Actions"} />
              <div className="space-y-1">
                <QuickActionRow
                  to={createPageUrl("Merchants")}
                  icon={Users}
                  iconBg={C.blueGlow}
                  iconColor={C.blue}
                  label={t("addMerchant")}
                  sublabel={t("onboardNewMerchant")}
                />
                <QuickActionRow
                  to={createPageUrl("Providers")}
                  icon={Server}
                  iconBg={C.emeraldSoft}
                  iconColor={C.emerald}
                  label={t("configureProvider")}
                  sublabel={t("addPaymentProvider")}
                />
                <QuickActionRow
                  to={createPageUrl("Routing")}
                  icon={TrendingUp}
                  iconBg={C.purpleSoft}
                  iconColor={C.purple}
                  label={t("routingRules")}
                  sublabel={t("configureRouting")}
                />
              </div>
            </GlassPanel>

            {/* ── Pending Alert ── */}
            <motion.div
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 28 }}
              className="rounded-2xl p-5 relative overflow-hidden cursor-pointer"
              style={{
                background: `linear-gradient(135deg,
                  rgba(251,191,36,0.12) 0%,
                  rgba(251,191,36,0.05) 60%,
                  transparent 100%)`,
                border: `1px solid rgba(251,191,36,0.22)`,
              }}
            >
              {/* Glow blob */}
              <div
                aria-hidden
                className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(251,191,36,0.18) 0%, transparent 70%)" }}
              />
              {/* Shimmer top */}
              <div
                className="absolute top-0 inset-x-0 h-px"
                style={{ background: "linear-gradient(90deg, transparent, rgba(251,191,36,0.5), transparent)" }}
              />

              <div className="relative flex items-start gap-3">
                <motion.div
                  animate={{ rotate: [0, -8, 8, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 3 }}
                  className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.25)" }}
                >
                  <Zap size={16} style={{ color: C.amber }} />
                </motion.div>
                <div>
                  <p className="text-sm font-bold" style={{ color: C.amber }}>
                    {t("pendingTransactions")}
                  </p>
                  <p className="text-[11px] mt-1 leading-relaxed font-medium" style={{ color: "rgba(251,191,36,0.6)" }}>
                    {t("requiresAttention")}
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </motion.div>
      </div>
    </div>
  );
}